import React, { useEffect, useState } from 'react';
import { Alert, Form, Input, InputNumber, Radio, Select, Switch, Table, Tabs } from 'antd';
import { getChargesTypesConstant, getPayoutCalcMethodConstant } from 'utils/apis/constants';
import {
  EXPENSE_SETTING_ROWS,
  PAYOUT_CALC_METHODS,
  chargeValueFromSetting,
  getDefaultChargesSetting,
  labelWithTooltip,
  popupContainer,
  tooltipLines,
  isUnitAvailableForPackage,
  unitSelectLabel,
  withUpdatedCharges,
} from './formShared';

const { TabPane } = Tabs;

const FALLBACK_PAYOUT_METHODS = Object.keys(PAYOUT_CALC_METHODS).map(code => ({
  value: code,
  label: PAYOUT_CALC_METHODS[code],
}));

const FALLBACK_CHARGE_TYPES = Object.keys(getDefaultChargesSetting()).map(key => ({
  key,
  label: key === 'cleaningFee' ? 'Cleaning Fee' : key === 'bookingEngineService' ? 'Booking Engine Service Fee' : key,
}));

const SHARE_OPTIONS = [
  { value: 'shared', label: 'Shared' },
  { value: 'host', label: 'Host' },
  { value: 'owner', label: 'Owner' },
];

const PAYOUT_TIPS = {
  perDayAndTransaction:
    'Payout uses the daily price of rental charges; other charges use the transaction date.',
  perTransaction: 'Payout is calculated from transactions in the payout window.',
  perCheckOut: 'Payout is calculated according to the guest checkout date.',
  perAverage: 'Payout is calculated from the average of all charges.',
};

const ShareRadios = ({ value, onChange }) => (
  <Radio.Group buttonStyle="solid" value={value} onChange={e => onChange(e.target.value)}>
    {SHARE_OPTIONS.map(opt => (
      <Radio.Button key={opt.value} value={opt.value}>
        {opt.label}
      </Radio.Button>
    ))}
  </Radio.Group>
);

const formatChargeTypes = raw => {
  if (!raw || typeof raw !== 'object') return FALLBACK_CHARGE_TYPES;
  return Object.keys(raw)
    .filter(key => raw[key] && raw[key].code !== 'addon' && raw[key].code !== 'stripeCharges')
    .map(key => ({
      key: raw[key].code,
      label: raw[key].label || raw[key].code,
      isCustom: raw[key].isCustom,
    }));
};

const formatPayoutMethods = raw => {
  if (!raw || typeof raw !== 'object') return FALLBACK_PAYOUT_METHODS;
  return Object.keys(raw)
    .map(key => ({
      value: raw[key] && raw[key].code,
      label: (raw[key] && raw[key].label) || (raw[key] && raw[key].code),
    }))
    .filter(opt => opt.value);
};

const ServicePackageFormFields = ({
  values,
  set,
  properties,
  propertiesLoading,
  units,
  unitsLoading,
  selectedUnitsForCurrentProperty,
  unitNameById,
  onPropertyChange,
  onUnitsChange,
  onGroupPackageChange,
  onUnitSharePercentageChange,
  assignedById,
}) => {
  const [chargeTypes, setChargeTypes] = useState(FALLBACK_CHARGE_TYPES);
  const [payoutMethods, setPayoutMethods] = useState(FALLBACK_PAYOUT_METHODS);

  useEffect(() => {
    let cancelled = false;
    getChargesTypesConstant()
      .then(res => {
        if (cancelled) return;
        const formatted = formatChargeTypes(res);
        if (formatted.length) setChargeTypes(formatted);
      })
      .catch(() => { });
    getPayoutCalcMethodConstant()
      .then(res => {
        if (cancelled) return;
        const formatted = formatPayoutMethods(res);
        if (formatted.length) setPayoutMethods(formatted);
      })
      .catch(() => { });
    return () => {
      cancelled = true;
    };
  }, []);

  const unitShareRateColumns = [
    {
      title: 'Unit',
      dataIndex: 'unitId',
      render: unitId => (unitNameById && unitNameById[String(unitId)]) || String(unitId),
    },
    {
      title: labelWithTooltip(
        'Owner Share %',
        tooltipLines([
          'Owner share for this unit. Host share is the remainder (100 − owner %).',
          'Example: 80% owner → host 20%.',
        ])
      ),
      dataIndex: 'ownerSharePercentage',
      render: (value, record) => (
        <InputNumber
          min={0}
          max={100}
          style={{ width: '100%' }}
          value={value}
          onChange={val => onUnitSharePercentageChange(record.unitId, val)}
        />
      ),
    },
    {
      title: 'Host Share %',
      render: record => `${100 - (Number(record.ownerSharePercentage) || 0)}%`,
    },
  ];

  const setExpense = key => val =>
    set('expensesSetting')(Object.assign({}, values.expensesSetting || {}, { [key]: val }));

  const setCharge = key => val => set('chargesSetting')(withUpdatedCharges(values.chargesSetting, key, val));

  const availableUnits = (units || []).filter(u => isUnitAvailableForPackage(u, assignedById));
  const unavailableUnits = (units || []).filter(u => !isUnitAvailableForPackage(u, assignedById));

  return (
    <Form layout="vertical">
      <Tabs defaultActiveKey="package">
        <TabPane tab="Package" key="package" forceRender>
          <Form.Item label="Name" required>
            <Input value={values.name} onChange={e => set('name')(e.target.value)} />
          </Form.Item>
          <Form.Item label="Property (to pick units)">
            <Select
              showSearch
              optionFilterProp="children"
              placeholder={propertiesLoading ? 'Loading properties...' : 'Select property'}
              value={values.propertyId}
              onChange={onPropertyChange}
              style={{ width: '100%' }}
              loading={propertiesLoading}
              getPopupContainer={popupContainer}
              notFoundContent={propertiesLoading ? 'Loading...' : 'No properties for this host'}
            >
              {(properties || []).map(p => (
                <Select.Option key={String(p._id)} value={String(p._id)}>
                  {p.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item label="Units">
            <Select
              mode="multiple"
              allowClear
              showSearch
              optionFilterProp="children"
              loading={unitsLoading}
              disabled={!values.propertyId}
              placeholder={values.propertyId ? 'Select units' : 'Select a property first'}
              value={selectedUnitsForCurrentProperty}
              onChange={onUnitsChange}
              style={{ width: '100%' }}
              getPopupContainer={popupContainer}
              notFoundContent={unitsLoading ? 'Loading...' : 'No units for this property'}
            >
              {availableUnits.length > 0 && (
                <Select.OptGroup label="Available">
                  {availableUnits.map(u => (
                    <Select.Option key={String(u._id)} value={String(u._id)}>
                      {unitSelectLabel(u, assignedById)}
                    </Select.Option>
                  ))}
                </Select.OptGroup>
              )}
              {unavailableUnits.length > 0 && (
                <Select.OptGroup label="Unavailable">
                  {unavailableUnits.map(u => (
                    <Select.Option key={String(u._id)} value={String(u._id)} disabled>
                      {unitSelectLabel(u, assignedById)}
                    </Select.Option>
                  ))}
                </Select.OptGroup>
              )}
            </Select>
            {(values.units || []).length > 0 && (
              <div style={{ marginTop: 8, color: 'rgba(0,0,0,0.45)' }}>
                Selected across properties: {(values.units || []).length} unit(s)
                {(values.units || []).some(id => !(units || []).some(u => String(u._id) === String(id)))
                  ? ' (includes units from other properties)'
                  : ''}
              </div>
            )}
          </Form.Item>
          <Form.Item
            label={labelWithTooltip(
              'Package Type',
              tooltipLines([
                'Pool rules apply to units that share a pool. Individual rules apply to selected units individually.',
                'Example: Unit A revenue 1,100 expenses 100, Unit B revenue 400 expenses 30, owner 70%.',
                'Pool profit share: (1,500 − 130) × 70% = 959, then split by days or SQFT.',
                'Individual: Unit A (1,100 − 100) × 70% = 700. Unit B (400 − 30) × 70% = 259.',
              ])
            )}
          >
            <Radio.Group
              buttonStyle="solid"
              value={values.groupingType || 'pool'}
              onChange={e => {
                const next = e.target.value;
                set('groupingType')(next);
                if (next === 'individual') set('shareProfitBySqft')(false);
              }}
            >
              <Radio.Button value="pool">Pool</Radio.Button>
              <Radio.Button value="individual">Individual</Radio.Button>
            </Radio.Group>
          </Form.Item>
          <Form.Item
            label={labelWithTooltip(
              'Share Type',
              tooltipLines([
                'Share by Profit: owner % of (revenue − expenses).',
                'Share by Revenue: owner % of revenue, then subtract expenses.',
                'Example: revenue 1,100, expenses 130, owner 70%.',
                'Profit → (1,100 − 130) × 70% = 679.',
                'Revenue → 1,100 × 70% − 130 = 640.',
              ])
            )}
          >
            <Select value={values.shareType} onChange={set('shareType')} style={{ width: '100%' }} getPopupContainer={popupContainer}>
              <Select.Option value="share-by-profit">Share by Profit</Select.Option>
              <Select.Option value="share-by-revenue">Share by Revenue</Select.Option>
            </Select>
          </Form.Item>
          {!values.isGroupPackage && (
            <Form.Item
              label={labelWithTooltip(
                'Owner Share %',
                tooltipLines([
                  'Applies to all units in this package. Host share is the remainder (100 − owner %).',
                  'Example: 70% owner → host 30%.',
                ])
              )}
            >
              <InputNumber min={0} max={100} style={{ width: '100%' }} value={values.ownerSharePercentage} onChange={set('ownerSharePercentage')} />
            </Form.Item>
          )}
          <Form.Item
            label={labelWithTooltip(
              'Allocate by SQFT',
              tooltipLines([
                'When enabled, pool profit sharing uses unit SQFT in addition to available days. Units without Unit SQFT cannot be added.',
                'Example: Unit A 1,000 SQFT and Unit B 500 SQFT, same available days → A gets 2/3 of the pool, B gets 1/3.',
              ])
            )}
          >
            <Switch
              checked={values.shareProfitBySqft}
              disabled={values.groupingType === 'individual'}
              onChange={set('shareProfitBySqft')}
            />
          </Form.Item>
          <Form.Item
            label={labelWithTooltip(
              'Group Package',
              tooltipLines([
                'When enabled, each unit in the pool can have its own owner share percentage. Revenue and expenses still share one pool.',
                'Example: Unit A 80% and Unit B 60% in the same package.',
              ])
            )}
          >
            <Switch checked={values.isGroupPackage} onChange={onGroupPackageChange} />
          </Form.Item>
          {values.isGroupPackage && (
            <Form.Item label="Unit share rates">
              <Table
                size="small"
                pagination={false}
                rowKey="unitId"
                dataSource={values.unitShareRates || []}
                columns={unitShareRateColumns}
                locale={{ emptyText: 'Select units to set per-unit owner share %' }}
              />
            </Form.Item>
          )}
          <Form.Item
            label={labelWithTooltip(
              'Agreement Type',
              tooltipLines([
                'Profit Sharing: owner % of profit or revenue, then expenses as configured.',
                'Fixed Rental: owner receives the monthly rental amount from Unit Config. Pool / Individual does not apply.',
              ])
            )}
          >
            <Select value={values.packageType} onChange={set('packageType')} style={{ width: '100%' }} getPopupContainer={popupContainer}>
              <Select.Option value="profit-sharing">Profit Sharing</Select.Option>
              <Select.Option value="tenancy-agreement">Fixed Rental</Select.Option>
            </Select>
          </Form.Item>
        </TabPane>

        <TabPane tab="Expenses Setting" key="expenses" forceRender>
          <p style={{ color: 'rgba(0,0,0,0.45)', marginBottom: 16 }}>
            This service package applies the following expenses sharing rules.
          </p>
          {EXPENSE_SETTING_ROWS.map(row => (
            <Form.Item key={row.key} label={row.label} style={{ marginBottom: 12 }}>
              <ShareRadios
                value={(values.expensesSetting && values.expensesSetting[row.key]) || 'shared'}
                onChange={setExpense(row.key)}
              />
            </Form.Item>
          ))}
        </TabPane>

        <TabPane tab="Charges (Income) Setting" key="charges" forceRender>
          <p style={{ color: 'rgba(0,0,0,0.45)', marginBottom: 16 }}>
            This service package applies the following income sharing rules.
          </p>
          {chargeTypes
            .filter(charge => charge.isCustom !== true).map(charge => (
              <Form.Item key={charge.key} label={charge.label} style={{ marginBottom: 12 }}>
                <ShareRadios
                  value={chargeValueFromSetting(values.chargesSetting, charge.key)}
                  onChange={setCharge(charge.key)}
                />
              </Form.Item>
            ))}
        </TabPane>

        <TabPane tab="Payout Configuration" key="payout" forceRender>
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
            message="Monthly Revenue, Refresh, and the owner statement use this method and start day (same as v1 payout). Start day 1 is the calendar month; start day 15 in August is 15 August to 14 September."
          />
          <p style={{ color: 'rgba(0,0,0,0.45)' }}>This service package applies the following payout configurations.</p>
          <Form.Item
            label={labelWithTooltip(
              'Payout Calculation Method',
              'Your payout will be calculated based on the method you chose.'
            )}
          >
            <Radio.Group
              buttonStyle="solid"
              value={values.payoutCalcMethod}
              onChange={e => set('payoutCalcMethod')(e.target.value)}
            >
              {payoutMethods.map(method => (
                <Radio.Button key={method.value} value={method.value} style={{ marginBottom: 8 }}>
                  {method.label}
                </Radio.Button>
              ))}
            </Radio.Group>
          </Form.Item>
          {values.payoutCalcMethod && PAYOUT_TIPS[values.payoutCalcMethod] && (
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
              message="Tips"
              description={PAYOUT_TIPS[values.payoutCalcMethod]}
            />
          )}
          <Form.Item
            label={labelWithTooltip(
              "When to start this month's payout calculation?",
              'The expected payout duration is from the date you set to the same date of next month (excluding that date). Example: 15 January to 14 February, or 1 January to end of January.'
            )}
          >
            <InputNumber
              min={1}
              max={28}
              style={{ width: '100%' }}
              value={values.startDay}
              onChange={set('startDay')}
            />
          </Form.Item>
          <Form.Item
            label={labelWithTooltip(
              'Hide Payout Calendar',
              'Stored for this package. Owner Reporting does not hide a payout calendar yet.'
            )}
          >
            <Switch checked={!!values.hidePayout} onChange={set('hidePayout')} />
          </Form.Item>
        </TabPane>
      </Tabs>
    </Form>
  );
};

export default ServicePackageFormFields;
